import { DAYS } from "../constants/timecard";
import { fmtStatusTime } from "./timecard";

// Pure helpers for the "Today" shift view: locating today's row, deriving the
// phase (idle/working/on-break/after-break/done) of each shift type, detecting
// whether a row has data, and formatting status/summary text. Extracted from
// TodaySection so they can be unit-tested in isolation.

export function getTodayRow(rows) {
  const d = DAYS[new Date().getDay()];
  return rows.find(r => r.day === d) ?? null;
}

export function getTodayRowIndex(rows) {
  const d = DAYS[new Date().getDay()];
  return rows.findIndex(r => r.day === d);
}

export function getRegularPhaseForRow(row) {
  if (!row) return null;
  if (!row.timeIn) return "idle";
  if (row.timeOut) return "done";
  if (!row.breakStart) return "working";
  if (!row.breakEnd) return "on-break";
  return "after-break";
}

export function getRegularPhase(rows) {
  return getRegularPhaseForRow(getTodayRow(rows));
}

export function getCallPhase(rows) {
  const row = getTodayRow(rows);
  if (!row) return null;
  if (!row.startTime) return "idle";
  if (!row.endTime) return "working";
  return "done";
}

export function getCallBackPhase(rows) {
  const row = getTodayRow(rows);
  if (!row) return null;
  if (!row.timeInOne) return "idle";
  if (!row.timeOutOne) return "working";
  if (row.timeInTwo && !row.timeOutTwo) return "working";
  return "done";
}

export function hasRegularShiftData(row) {
  if (!row) return false;
  return !!(row.timeIn || row.timeOut || row.breakStart || row.hospital || row.department || row.notes);
}

export function hasCallShiftData(row) {
  if (!row) return false;
  return !!(row.startTime || row.endTime || row.hospital || row.department);
}

export function hasCallBackShiftData(row) {
  if (!row) return false;
  return !!(row.timeInOne || row.timeOutOne || row.timeInTwo || row.timeOutTwo);
}

export const SHIFT_LABELS = { regular: "Regular", call: "On Call", callback: "Call Back" };
export const ACTIVE_PHASES = new Set(["working", "on-break", "after-break"]);

export function getShiftStatus(phase) {
  if (!phase || phase === "idle") return null;
  if (phase === "working" || phase === "after-break") return { label: "Active", variant: "active" };
  if (phase === "on-break") return { label: "On break", variant: "on-break" };
  if (phase === "done") return { label: "Completed", variant: "completed" };
  return null;
}

export function getTimeRangeSummary(type, row, timeFormat) {
  const f = v => fmtStatusTime(v, timeFormat);
  if (type === "regular") {
    if (!row?.timeIn) return null;
    return row.timeOut ? `${f(row.timeIn)} – ${f(row.timeOut)}` : `${f(row.timeIn)} –`;
  }
  if (type === "call") {
    if (!row?.startTime) return null;
    return row.endTime ? `${f(row.startTime)} – ${f(row.endTime)}` : `${f(row.startTime)} –`;
  }
  if (type === "callback") {
    if (!row?.timeInOne) return null;
    const p1 = row.timeOutOne
      ? `${f(row.timeInOne)} – ${f(row.timeOutOne)}`
      : `${f(row.timeInOne)} –`;
    if (!row.timeInTwo) return p1;
    const p2 = row.timeOutTwo
      ? `${f(row.timeInTwo)} – ${f(row.timeOutTwo)}`
      : `${f(row.timeInTwo)} –`;
    return `${p1}, ${p2}`;
  }
  return null;
}

export function getClockStatusText(type, phase, row, timeFormat) {
  const f = v => fmtStatusTime(v, timeFormat);
  if (type === "regular") {
    if (phase === "working") return row?.timeIn ? `Clocked in at ${f(row.timeIn)}` : "Currently working";
    if (phase === "on-break") return row?.breakStart ? `On break since ${f(row.breakStart)}` : "On break";
    if (phase === "after-break") return row?.breakEnd ? `Break ended at ${f(row.breakEnd)}` : "Working";
  }
  if (type === "call") {
    if (phase === "working") return row?.startTime ? `Started at ${f(row.startTime)}` : "On call";
  }
  if (type === "callback") {
    if (phase === "working") {
      const since = row?.timeInTwo || row?.timeInOne;
      return since ? `Called back at ${f(since)}` : "On callback";
    }
  }
  return null;
}

export function getBreakInfo(type, row, timeFormat) {
  if (type !== "regular" || !row?.timeIn) return null;
  const f = v => fmtStatusTime(v, timeFormat);
  if (!row.breakStart) return "Not started";
  if (!row.breakEnd) return `Started ${f(row.breakStart)}`;
  return `${f(row.breakStart)} – ${f(row.breakEnd)}`;
}
